<template>
  <div class="patients-list">
    <v-row class="patients-list_header" align="center">
      <v-col cols="6" class="patients-list_header_block">
        <h1>{{ $t("patients.title") }}</h1>
      </v-col>
      <v-col cols="6" class="d-flex align-start patients-list_header_block">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          solo
          dense
          hide-details
          ontouchstart="this.removeAttribute('readonly');"
          onfocus="this.removeAttribute('readonly');"
          :placeholder="$t('patients.searchPlaceholder')"
        >
        </v-text-field>
        <v-btn height="38" class="ml-4 btn" color="success" @click="toggleDialog">
          <v-icon> mdi-plus </v-icon>
          {{ $t("patients.invite.button") }}
        </v-btn>
        <v-btn height="38" class="ml-4 btn" color="success" @click="toggleImportDialog" v-if="showImport">
          <v-icon> mdi-import </v-icon>
          {{ $t("patients.import.button") }}
        </v-btn>
      </v-col>
    </v-row>
    <v-tabs v-model="tab" class="tabs" active-class="tabs_tab_active" color="primary">
      <v-tab class="tabs_tab"> {{ $t("patients.activeUsers") }} ({{ patients.length }})</v-tab>
      <v-tab class="tabs_tab"> {{ $t("patients.invitations") }} ({{ invites.length }})</v-tab>
      <v-tab-item class="patients-list_tabs_tab_item">
        <PatientsListTable
          :loading="loading"
          :headers="patientsHeaders"
          :items="tablePatients"
          :search="search"
          :actions="actions.patients"
        />
      </v-tab-item>
      <v-tab-item class="patients-list_tabs_tab_item">
        <PatientsListTable
          :loading="loading"
          :headers="invitesHeaders"
          :items="tableInvites"
          :search="search"
          :actions="actions.invites"
        />
      </v-tab-item>
    </v-tabs>
    <InvitePatientDialog v-model="dialog" @toggleDialog="toggleDialog" />
    <ImportPatientDialog v-model="importDialog" @toggleDialog="toggleImportDialog" v-if="showImport" />
    <AssignPatientDialog v-model="assignDialog" :patient="assignPatient" />
    <Confirm
      :dialog="cancelDialog"
      :title="$t('patients.cancelDialog.title')"
      :text="$t('patients.cancelDialog.text')"
      :confirmText="$t('patients.cancelDialog.confirm')"
      :cancelText="$t('general.dialog.cancel')"
      confirm-color="primary"
      @toggleDialog="cancelDialog = false"
      @confirm="cancelInvitation"
    />
    <SuccessDialog
      :dialog="successDialog"
      :text="$t(`patients.invite.resendSuccess`)"
      @on-close="successDialog = false"
    />
  </div>
</template>
<script>
import { DateTime } from "luxon";
import { mapActions, mapGetters } from "vuex";

import AssignPatientDialog from "@/components/AssignPatientDialog/index";
import ImportPatientDialog from "@/components/ImportPatientDialog/index";
import InvitePatientDialog from "@/components/InvitePatientDialog/index";
import Confirm from "@/components/shared/Confirm";
import SuccessDialog from "@/components/shared/SuccessDialog";
import { snackBarEventBus, snackBarEventName } from "@/eventBuses/snackBar.eventBus";
import { RolesEnum } from "@/types/Roles.enum";
import { getStateName } from "@/utils/states.ts";

import { invitesHeaders, patientsHeaders } from "./constants.ts";
import PatientsListTable from "./Table.vue";

export default {
  name: "PatientsList",
  components: {
    PatientsListTable,
    InvitePatientDialog,
    ImportPatientDialog,
    AssignPatientDialog,
    Confirm,
    SuccessDialog,
  },
  data() {
    return {
      successDialog: false,
      loading: false,
      patientsHeaders,
      invitesHeaders,
      dialog: false,
      importDialog: false,
      cancelDialog: false,
      assignDialog: false,
      assignPatient: null,
      invitationToCancel: null,
      tab: null,
      search: "",
      actions: {
        patients: [
          {
            callback: this.openAssignDialog,
            text: this.$t("patients.table.assignPatient"),
          },
        ],
        invites: [
          {
            callback: this.openCancelDialog,
            text: this.$t("patients.table.cancelInvitation"),
          },
          {
            callback: this.resendInvitation,
            text: this.$t("patients.table.resendInvitation"),
          },
        ],
      },
    };
  },
  computed: {
    ...mapGetters(["role"]),
    ...mapGetters("invites", ["invites"]),
    ...mapGetters("patients", ["patients"]),
    tablePatients() {
      return this.patients.map((p) => {
        const practitionerName = p.practitioner ? `${p.practitioner.firstName} ${p.practitioner.lastName}` : "";
        return {
          ...p,
          name: `${p.firstName} ${p.lastName}`,
          state: getStateName(p.state),
          birthDate: DateTime.fromISO(p.birthDate).toFormat("M/dd/yyyy"),
          practitioner: practitionerName,
          checked: false,
        };
      });
    },
    tableInvites() {
      return this.invites.map((i) => ({
        ...i,
        name: `${i.firstName} ${i.lastName}`,
        status: i.isExpired ? this.$t("patients.table.expired") : this.$t("patients.table.pending"),
        checked: false,
      }));
    },
    showImport() {
      return this.role === RolesEnum.Owner || this.role === RolesEnum.Registrar;
    },
  },
  methods: {
    ...mapActions("invites", ["sendInvite", "getAllInvites", "deleteInvitation", "resendInvite"]),
    ...mapActions("patients", ["getAllPatients"]),
    toggleDialog() {
      this.dialog = !this.dialog;
    },
    toggleImportDialog() {
      this.importDialog = !this.importDialog;
    },
    openAssignDialog(item) {
      this.assignDialog = true;
      this.assignPatient = item;
    },
    openCancelDialog(item) {
      this.invitationToCancel = item;
      this.cancelDialog = true;
    },
    async cancelInvitation() {
      await this.deleteInvitation(this.invitationToCancel.id);
      this.cancelDialog = false;
    },
    async resendInvitation(item) {
      await this.resendInvite(item);
      this.successDialog = true;
    },
  },
  async mounted() {
    try {
      this.loading = true;
      await this.getAllInvites();
      await this.getAllPatients();
    } catch (err) {
      snackBarEventBus.$emit(snackBarEventName, { message: err.message || err, type: "error" });
    }
    this.loading = false;
  },
};
</script>
<style lang="scss">
.patients-list {
  padding: 0 30px;
  &_header {
    @media (max-width: 480px) {
      flex-direction: column;
      margin: 0;
    }
    &_actions {
      display: flex;
      align-items: flex-start;
    }
    &_block {
      @media (max-width: 480px) {
        max-width: 100%;
      }
    }
  }
  &_tabs {
    .v-tabs-bar {
      border-bottom: 1px solid $primarylight;
    }

    &_tab {
      #text {
        font-style: normal;
        font-weight: 500;
        font-size: 14px;
        line-height: 17px;

        color: $primaryblack;

        mix-blend-mode: normal;
      }

      &_item {
        padding: 2rem 2rem 0 2rem;
      }
    }
  }
}

.success-dialog {
  display: flex !important;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  img {
    display: inline-block;
  }
  &_title {
    font-style: normal;
    font-weight: 500;
    font-size: 21px;
    line-height: 26px;
    text-align: center;
    word-break: break-word !important;
  }
  &_close-icon {
    top: 10px;
    right: 10px;
  }
}
</style>
